import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { User } from '../types';
import { apiService } from '../services/api';

interface UserContextType {
  user: User | null;
  loading: boolean;
  refreshUser: () => Promise<void>;
  updateUser: (updatedUser: User) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { user: auth0User, getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserData = useCallback(async () => {
    if (!isAuthenticated || !auth0User) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Get access token and set it in API service
      const token = await getAccessTokenSilently();
      apiService.setAuthToken(token);
      
      // Fetch user data from our backend
      const userData = await apiService.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [auth0User, getAccessTokenSilently, isAuthenticated]);

  const refreshUser = async () => {
    await fetchUserData();
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const value: UserContextType = {
    user,
    loading,
    refreshUser,
    updateUser,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}; 